import type { CostCategory } from '@/types/economics';
import { currencySymbol } from '@/utils';

/**
 * Categorical colours for cost categories, in a fixed order: a category keeps
 * its colour whatever is filtered, so a reader who learned "partner fees are
 * blue" is never misled. The six are the first six slots of a validated
 * categorical palette (adjacent pairs: colourblind ΔE ≥ 9.1, normal-vision
 * ΔE ≥ 19.6 on a light surface). Aqua, yellow and magenta sit below 3:1 on
 * white, so a chart using them always ships a legend and a table view — the
 * value is never readable from colour alone.
 */
export const CATEGORY_COLORS: Readonly<Record<CostCategory, string>> = {
  partner: '#2a78d6',
  qa: '#eb6834',
  llm: '#1baf7a',
  external_api: '#eda100',
  registry: '#e87ba4',
  monitoring: '#008300',
};

/** One series, one colour: the first slot. */
export const SINGLE_SERIES_COLOR = CATEGORY_COLORS.partner;

/** Compact axis figures: 0 / 950 / 1.2K / 3.4M. */
export function compactNumber(value: number): string {
  return new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 }).format(
    value
  );
}

/** Up to five clean ticks from zero to at least `max`. */
export function niceTicks(max: number): number[] {
  if (max <= 0) {
    return [0];
  }
  const rough = max / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= rough) ?? rough;
  const ticks: number[] = [];
  for (let tick = 0; tick < max + step; tick += step) {
    ticks.push(Number(tick.toPrecision(12)));
  }
  return ticks;
}

/** "£1,234.50": always two places, so a cost of £1.60 never reads as £1.6. */
export function formatAmount(value: number, currency: string): string {
  const amount = value.toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${currencySymbol(currency) || `${currency} `}${amount}`;
}
